/** @jsxImportSource @emotion/react */
import React, {useEffect} from "react";
import {useDispatch, useSelector} from "react-redux";
import {createSelector} from "reselect";
import {Trans, useTranslation} from "react-i18next";
import {Spinner} from "@blueprintjs/core";
import moment from "moment";
import {orderBy} from "lodash";

// Libs
import Button from "../common/components/button";
import {boxShadow, primaryColor} from "../common/libs/theme-lib";

// Components
import ClientSelector from "../common/components/client-selector";
import VirtualizedTable from "../common/components/virtualized-table";
import DownloadStocksPopover from "./download-stocks-popover";

// Hooks
import {useAuth} from "../auth/auth";
import {useUserClientsData} from "../user-clients-data/user-clients-data";

// Actions
import {downloadPdf} from "../content/content-actions";
import {readStocks} from "./stocks-actions";

// Selectors
import {stocksSelector, stocksIsFetchingSelector, stocksErrorSelector} from "./stocks-selectors";
import {contentIsFetchingSelector, contentIdSelector, contentTypeSelector} from "../content/content-selectors";

// Component Selector
const stocksPageSelector = createSelector(
    stocksSelector,
    stocksIsFetchingSelector,
    stocksErrorSelector,
    contentIsFetchingSelector,
    contentIdSelector,
    contentTypeSelector,
    (stocks, stocksIsFetching, stocksError,contentIsFetching, contentId, contentType) => {
        stocks = stocks?.toJS() || [];
        stocks = orderBy(stocks, ["time"], "desc");
        stocks = orderBy(stocks, ["date"], "desc");
        const lastFileStocks = stocks[0];
        stocks = stocks.slice(1);
        return {
            stocks: stocks,
            lastFileStocks: lastFileStocks,
            stocksIsFetching,
            stocksError,
            contentIsFetching,
            contentId,
            contentType
        }
    }
);

// Redux dispatch hook
const useReduxDispatch = () => {
    const dispatch = useDispatch();
    return {
        downloadPdf: (pdfId, pdfType, clientId) => dispatch(downloadPdf(pdfId, pdfType, clientId)),
        readStocks: (filters) => dispatch(readStocks(filters))
    }
};

const StocksPage = (props) => {

    // Client Selector
    const {selectedClient, userType} = useAuth();
    const {userClientsData} = useUserClientsData();
    const selectedClientData = userClientsData.find(client => client._id === selectedClient);

    // Redux
    const {
        stocks, lastFileStocks,
        contentIsFetching, contentId, contentType
    } = useSelector(state => stocksPageSelector(state));
    const {readStocks, downloadPdf} = useReduxDispatch();

    useEffect(() => {
        // readStocks if no stocks data
        if (stocks.length === 0) readStocks({client: selectedClient});
    }, [selectedClient]);

    //i18n
    const {t} = useTranslation("stocks");

    return (
        <>
            <div style={{
                display: "flex",
                alignItems: "flex-end",
                paddingBottom: "15px"
            }}>
                <div style={{flex: 1}}>
                    <div style={{
                        color: primaryColor,
                        fontSize: 28,
                        fontWeight: "bold",
                        marginBottom: 10
                    }}>{t("title", { client: selectedClientData && selectedClientData.name } )}</div>
                    <div style={{fontWeight: 100, fontSize: 18}}>{t("description")}</div>
                </div>
                {userType === "admin" && (
                    <ClientSelector style={{marginRight: 0}} />
                )}
            </div>
            <div style={{
                display: "flex",
                flex: 1,
            }}>
                {lastFileStocks && (
                    <div style={{
                        backgroundColor: "white",
                        boxShadow: boxShadow,
                        padding: 15,
                        display: "flex",
                        flexDirection: "column",
                        height: "min-content",
                        marginRight: 15
                    }}>
                        <p style={{marginBottom: 0}}><strong>{t("last.recent")}</strong></p>
                        <div style={{
                            margin: "30px auto",
                            display: "flex",
                            justifyContent: "space-evenly",
                            width: "calc(100% - 30px)"
                        }}>
                            <strong style={{color: primaryColor}}>{t("last.date")}</strong>
                            <span>{moment(lastFileStocks.date).format("DD/MM/YYYY")} {lastFileStocks.time}</span>
                        </div>
                        <DownloadStocksPopover data={lastFileStocks}>
                            <Button
                                text={t("downloadButton")}
                                icon="download"
                                intent="primary"
                                style={{
                                    maxWidth: 250,
                                    margin: "auto"
                                }}
                            />
                        </DownloadStocksPopover>
                    </div>
                )}
                <div style={{
                    backgroundColor: "white",
                    boxShadow: boxShadow,
                    padding: 15,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column"
                }}>
                    <div style={{marginBottom: 15, fontSize: 18, fontWeight: 100}}>
                        <Trans
                            i18nKey="stocks:stocksTableHeader"
                            values={{count: stocks.length}}
                            components={[<strong>number</strong>]}
                        />
                    </div>
                    <VirtualizedTable
                        config={{
                            sortable: true,
                            showFooter: true
                        }}
                        columns={[{
                            id: "date",
                            Header: t("stocksTableColumns.date"),
                            accessor: row =>
                                [row.date, row.time].reduce(
                                    (date, time) => moment(date).format("DD/MM/YYYY") + ' ' + time
                                ),
                        }, {
                            id: "file_name",
                            Header: t("stocksTableColumns.download"),
                            accessor: row => row,
                            type: "function",
                            disableSortBy: true,
                            minWidth: 250,
                            width: 250,
                            style: {
                                maxWidth: 60,
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center"
                            },
                            nodeContent: (row) => {
                                return contentIsFetching && contentType === "stock" && contentId === row.file_name ? (
                                    <div>
                                        <Spinner
                                            size={20}
                                            intent={"danger"}
                                        />
                                    </div>
                                ) : (
                                    <div 
                                        css={{
                                            cursor: "pointer",
                                            transition: "all 0.4s",
                                            "&:hover": {
                                                color: primaryColor
                                            }
                                        }}
                                    >
                                        <DownloadStocksPopover data={row} />
                                    </div>
                                );
                            }
                        }]}
                        data={stocks}
                    />
                </div>
            </div>
        </>
    );
};

export default StocksPage;